import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckboxElement,
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { agentDocumentsByAgentId, getLLMAgent } from "../graphql/queries";
import { createLLMAgentMCP, updateLLMAgentMCP } from "../graphql/mutations";
import {
  AgentDocumentsByAgentIdQuery,
  GetLLMAgentDocumentQuery,
  GetLLMAgentQuery,
  LLMEmailCaptureMode,
  LLMVisibility,
} from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { useUser } from "../contexts/useUser";
import { updateLLMAgentAfterEdit } from "../dataaccess/updateLLMAgentAfterEdit";
import { createLLMAgentAfterEdit } from "../dataaccess/createLLMAgentAfterEdit";
import { ChatPanel } from "llmasaservice-ui";
import {
  materialDark,
  materialLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import FileUploader from "./FileUploader";
import UrlScrape from "./UrlScrape";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { isProduction } from "../enterprise/tokenmanagement";

const initialValues = {
  name: "",
  status: "active",
  description: "",
  systemInstructions: "",
  systemInstructionsAction: "append",
  groupId: "",

  displayTheme: "dark",
  cssUrl: "",
  displayTitle: "Your agent",
  displayStartMessageOrPrompt: "message",
  displayInitialMessageOrPrompt: "Enter your prompt here",
  displayPromptTemplate: "{{prompt}}",
  displayFollowOnPrompts: "summarize",
  displayActions: "[]",
  displayWidth: "100%",
  displayHeight: "100vh",
  data: "",
  displayPlaceholder: "Type a message",
  displayHideInitialPrompt: true,
  displayShowSaveButton: true,
  displayShowEmailButton: true,
  displayShowCallToAction: false,
  displayCallToActionButtonText: "Submit",
  displayCallToActionEmailAddress: "",
  displayCallToActionEmailSubject: "Agent CTA submitted",
  displayScrollToEnd: false,
  ragQueryLimit: 10,
  ragRankLimit: 5,
  ragIncludeCitations: false,
  ragSkipChecksOnContext: true,

  visibility: LLMVisibility.ALL,
  createConversationOnFirstChat: true,
  customerEmailCaptureMode: LLMEmailCaptureMode.HIDE,
  customerEmailCapturePlaceholder: "Please enter your email...",
};

const schema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, "Name is required"),
    status: z.string().min(1, "Status is required"),
    description: z.string().optional().nullable(),

    systemInstructions: z.string().optional().nullable(),
    systemInstructionsAction: z.string().optional().nullable(),
    groupId: z.string().optional().nullable(),

    displayTheme: z.string().optional().nullable(),
    cssUrl: z.string().optional().nullable(),
    displayTitle: z.string().optional().nullable(),
    displayStartMessageOrPrompt: z.string().optional().nullable(),
    displayInitialMessageOrPrompt: z.string().optional().nullable(),
    displayPromptTemplate: z.string().optional().nullable(),
    displayFollowOnPrompts: z.string().optional().nullable(),
    displayActions: z.string().optional().nullable(),
    displayWidth: z.string().optional().nullable(),
    displayHeight: z.string().optional().nullable(),
    data: z.string().optional().nullable(),
    displayPlaceholder: z.string().optional().nullable(),
    displayHideInitialPrompt: z.boolean().optional().nullable(),
    displayShowSaveButton: z.boolean().optional().nullable(),
    displayShowEmailButton: z.boolean().optional().nullable(),
    displayShowCallToAction: z.boolean().optional().nullable(),
    displayCallToActionButtonText: z.string().optional().nullable(),
    displayCallToActionEmailAddress: z.string().optional().nullable(),
    displayCallToActionEmailSubject: z.string().optional().nullable(),
    displayScrollToEnd: z.boolean().optional().nullable(),
    visibility: z.any().optional().nullable(),
    createConversationOnFirstChat: z.boolean().optional().nullable(),
    customerEmailCaptureMode: z.any().optional().nullable(),
    customerEmailCapturePlaceholder: z.string().optional().nullable(),
    ragIncludeCitations: z.boolean().optional().nullable(),
    ragSkipChecksOnContext: z.boolean().optional().nullable(),
    ragQueryLimit: z
      .number()
      .min(5, "Query limit should be greater or equal to 10. If unsure use 10.")
      .max(50, "Query limit should be less than 50. If unsure use 10.")
      .optional()
      .nullable(),
    ragRankLimit: z
      .number()
      .min(3, "Rerank limit should be greater or equal to 3. If unsure use 5.")
      .max(50, "Rerank limit should be less than 50. If unsure use 5.")
      .optional()
      .nullable(),
  })
  .refine(
    (data) =>
      data.ragRankLimit === null ||
      data.ragRankLimit === undefined ||
      data.ragRankLimit <= (data.ragQueryLimit ?? 5),
    {
      message: "Rerank limit must be less than or equal to Query limit",
      path: ["ragRankLimit"],
    }
  );

type AgentFormType = z.infer<typeof schema>;

const AgentForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();
  const { project, entitlements, trialDaysRemaining, subscriptionActive } =
    useUser();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const [staticUrl, setStaticUrl] = React.useState<string | null>(null);
  const [staticHeight, setStaticHeight] = React.useState("600");
  const [styleText, setStyleText] = React.useState("");
  const [apiKey1, setApiKey1] = React.useState("");
  const [mcpUrl, setMcpUrl] = React.useState("");
  const [mcpUrlError, setMcpUrlError] = React.useState(false);
  const [mcpApiKey, setMcpApiKey] = React.useState("");
  const [showMcpApiKey, setShowMcpApiKey] = React.useState(false);
  const [openMCPDialog, setOpenMCPDialog] = React.useState(false);
  const [showDeletedMCPs, setShowDeletedMCPs] = useState(false);
  const [executionMode, setExecutionMode] = React.useState("CLIENT");

  const getAPIKeys = () => {
    console.log(project?.id);

    API.get("eventapi", "/getIntegrationApiKeys", {
      queryStringParameters: {
        projectId: project?.id,
      },
    })
      .then((response) => {
        if (response.success) {
          const keys = JSON.parse(response.body);

          console.log(keys);

          if (!keys || !keys.key1) {
            createAPIKeys();
          } else {
            setApiKey1(keys.key1);
            //setApiKey2(keys.key2);
            //setKey1LastRotated(keys.key1LastRotated);
            //setKey2LastRotated(keys.key2LastRotated);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const createAPIKeys = async () => {
    const response = await API.post("eventapi", "/createIntegrationApiKeys", {
      body: {
        projectId: project?.id,
        projectName: project?.name,
      },
    });

    if (response.success) {
      const keys = JSON.parse(response.body);
      setApiKey1(keys.key1);
      //setApiKey2(keys.key2);
      //setKey1LastRotated(keys.key1LastRotated);
      //setKey2LastRotated(keys.key2LastRotated);
    }
  };

  useEffect(() => {
    if (!apiKey1 || apiKey1 === "") {
      getAPIKeys();
    }
  }, []);

  const queryClient = useQueryClient();

  const mutationUpdateAgent = useMutation({
    mutationFn: updateLLMAgentAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating agent", error as any);
    },
  });

  const mcpCreateMutation = useMutation({
    mutationFn: ({ mcpUrl, executionMode, accessToken }: { mcpUrl: string, executionMode: string, accessToken?: string }) =>
      API.graphql(
        graphqlOperation(createLLMAgentMCP, {
          input: {
            agentId: id,
            projectId: project?.id,
            status: "active",
            url: mcpUrl,
            executionMode: executionMode,
            accessToken: accessToken || null,
          },
        })
      ) as Promise<any>,

    onSuccess: () => {
      setOpenMCPDialog(false);
      setMcpUrl("");
      setMcpApiKey("");
      setShowMcpApiKey(false);
      setExecutionMode("CLIENT");
      //queryClient.invalidateQueries({
      //  queryKey: ["agents", { id: id }],
      //});
    },
    onError: (err: any) => {
      logError("Error adding MCP client", err as any);
    },
  });

  const mutationCreateAgent = useMutation({
    mutationFn: createLLMAgentAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error creating agent", error as any);
    },
  });

  const {
    data: agentData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["agents", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMAgent, { id: id })) as Promise<
        GraphQLResult<GetLLMAgentQuery>
      >,
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: groupData,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const { data: documentsData, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ["LLMAgentDocuments", { agentId: id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(agentDocumentsByAgentId, { agentId: id })
      ) as Promise<GraphQLResult<AgentDocumentsByAgentIdQuery>>,
    enabled: !!id,
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<AgentFormType>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  // Reset form when agent data loads
  useEffect(() => {
    if (agentData?.data?.getLLMAgent) {
      const agentFormData = {
        ...initialValues,
        ...agentData.data.getLLMAgent,
        ragSkipChecksOnContext: agentData.data.getLLMAgent.ragSkipChecksOnContext ?? true,
      };
      reset(agentFormData);
    } else if (!id) {
      // Reset to initial values for new agent
      reset(initialValues);
    }
  }, [agentData?.data?.getLLMAgent , id, reset]);

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading agent</div>;

  const handleUpdate = (values: AgentFormType) => {
    if (!values) return;

    try {
      mutationUpdateAgent.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating agent", error as any);
    }
  };

  const handleCreate = (values: AgentFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateAgent.mutate({
      projectId: project?.id,
      ...values,
    });
  };

  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: AgentFormType) => {
    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  const themeWatch = watch("displayTheme");
  const cssUrlWatch = watch("cssUrl");
  const widthWatch = watch("displayWidth");
  const heightWatch = watch("displayHeight");
  const titleWatch = watch("displayTitle");
  const initialWatch = watch("displayInitialMessageOrPrompt");
  const startWatch = watch("displayStartMessageOrPrompt");
  const promptTemplateWatch = watch("displayPromptTemplate");
  const followOnPromptsWatch = watch("displayFollowOnPrompts");
  const actionsWatch = watch("displayActions");
  const displayPlaceholderWatch = watch("displayPlaceholder");
  const displayHideInitialPromptWatch = watch("displayHideInitialPrompt");
  const displayShowSaveButtonWatch = watch("displayShowSaveButton");
  const displayShowEmailButtonWatch = watch("displayShowEmailButton");
  const displayShowCallToActionWatch = watch("displayShowCallToAction");
  const displayCallToActionButtonTextWatch = watch(
    "displayCallToActionButtonText"
  );
  const displayCallToActionEmailAddressWatch = watch(
    "displayCallToActionEmailAddress"
  );
  const displayCallToActionEmailSubjectWatch = watch(
    "displayCallToActionEmailSubject"
  );
  const dataWatch = watch("data");
  const groupIdWatch = watch("groupId");
  const displayScrollToEndWatch = watch("displayScrollToEnd");

  const ragQueryLimitWatch = watch("ragQueryLimit");
  const ragRankLimitWatch = watch("ragRankLimit");

  const createConversationOnFirstChatWatch = watch(
    "createConversationOnFirstChat"
  );
  const customerEmailCaptureModeWatch = watch("customerEmailCaptureMode");
  const customerEmailCapturePlaceholderWatch = watch(
    "customerEmailCapturePlaceholder"
  );

  const getActionsArraySafely = (actionsString: string) => {
    let actions: any[] = [];
    if (actionsString && actionsString !== "") {
      try {
        actions = JSON.parse(actionsString);
        if (!Array.isArray(actions)) {
          throw new Error("Parsed actions is not an array");
        }
      } catch (error) {
        actions = [];
      }
    }
    return actions;
  };

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const options = [
    { id: "", label: "Default" },
    ...(groupData?.data?.groupsByProjectId?.items?.map((group) => ({
      id: group?.id ?? "",
      label: group?.name ?? "",
    })) ?? []),
  ];

  const createStaticHTMLPage = async () => {
    if (!id) return;

    console.log(staticHeight);

    try {
      await API.post("eventapi", "/agentStaticPage", {
        body: {
          agentId: id,
          title: titleWatch,
          width: widthWatch,
          height: heightWatch,
          htmlHeight: staticHeight,
          origin: window.location.origin,
        },
      });

      setStaticUrl("https://agent.llmasaservice.io/" + id + ".html");
    } catch (error) {
      console.error("Error creating static page:", error);
    }
  };

  const isValidUrl = (url: string) => {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])?)\\.)+[a-z]{2,}|" + // domain name
        "localhost|" + // localhost
        "\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}|" + // OR ipv4
        "\\[([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}\\])" + // OR ipv6
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i"
    ); // fragment locator
    return !!pattern.test(url);
  };

  return (
    <>
      <Paper
        sx={{
          margin: 2,
          padding: 2,
          borderRadius: 3,
          border: "1px solid #444",
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(event: React.SyntheticEvent, newValue: number) =>
            setSelectedTab(newValue)
          }
        >
          <Tab label="Settings" />
          <Tab label="UI Panel" />
          <Tab label="Data" />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                Documents&nbsp;{" "}
                <Chip
                  size="small"
                  label="dev"
                  title="Developer level feature"
                  color={subscriptionActive ? "default" : "warning"}
                />
              </Box>
            }
            disabled={!id || !(trialDaysRemaining > 0 || subscriptionActive)}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                MCP Servers&nbsp;{" "}
                <Chip
                  size="small"
                  label="ent (alpha)"
                  title="ALPHA Enterprise level feature"
                  color={subscriptionActive ? "default" : "warning"}
                />
              </Box>
            }
            disabled={!id || !(trialDaysRemaining > 0 || subscriptionActive)}
          />

          {!isProduction && <Tab label="Style" />}
        </Tabs>

        <Grid container>
          <Grid item xs={12} md={9}>
            {selectedTab === 0 && (
              <>
                <Grid container spacing={2} mt={2}>
                  <Grid item xs={12} md={3}>
                    <TextFieldElement
                      control={control}
                      name="name"
                      label="Name"
                      size="small"
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectElement
                      name="groupId"
                      label="Service Group (blank for default routing)"
                      control={control}
                      size="small"
                      fullWidth
                      options={options}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectElement
                      control={control}
                      name="status"
                      label="Agent status"
                      size="small"
                      required
                      fullWidth
                      options={[
                        { id: "active", label: "Active" },
                        { id: "disabled", label: "Disabled" },
                        { id: "deleted", label: "Deleted" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectElement
                      control={control}
                      name="visibility"
                      label="Agent visibility"
                      size="small"
                      fullWidth
                      options={[
                        { id: LLMVisibility.PUBLIC, label: "Public" },
                        { id: LLMVisibility.ENTERPRISE, label: "Enterprise" },
                        { id: LLMVisibility.APPLICATION, label: "Application" },
                        { id: LLMVisibility.ALL, label: "All" },
                      ]}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="description"
                    label="Description / Purpose"
                    size="small"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} mt={2}>
                  <TextareaAutosizeElement
                    control={control}
                    name="systemInstructions"
                    label="Agent System Instructions"
                    size="small"
                    fullWidth
                    rows={15}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <SelectElement
                    control={control}
                    name="systemInstructionsAction"
                    label="System Instructions Action"
                    size="small"
                    required
                    fullWidth
                    options={[
                      {
                        id: "append",
                        label: "Append to project instructions",
                      },
                      {
                        id: "replace",
                        label: "Replace project instructions",
                      },
                    ]}
                  />
                </Grid>
              </>
            )}

            {selectedTab === 1 && (
              <>
                <Grid container mt={2} spacing={2}>
                  <Grid item xs={4}>
                    <TextFieldElement
                      control={control}
                      name="displayTitle"
                      label="What title do you want on the panel?"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <SelectElement
                      control={control}
                      name="displayTheme"
                      label="Dark or light panel theme?"
                      size="small"
                      fullWidth
                      options={[
                        { id: "dark", label: "Dark theme" },
                        { id: "light", label: "Light theme" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextFieldElement
                      control={control}
                      name="cssUrl"
                      label="URL to CSS file (leave blank for default)"
                      size="small"
                      fullWidth
                    />
                    <Link
                      href="https://help.llmasaservice.io/docs/developers/customthemes"
                      target="_blank"
                    >
                      <Typography variant="caption">
                        Learn more about custom CSS themes
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
                <Grid container mt={2} spacing={2}>
                  <Grid item xs={3}>
                    <TextFieldElement
                      control={control}
                      name="displayWidth"
                      label="Width (use px or %, e.g. 100vw)"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldElement
                      control={control}
                      name="displayHeight"
                      label="Height (use px or %, e.g. 100vh)"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextFieldElement
                      control={control}
                      name="displayPlaceholder"
                      label="Prompt placeholder text"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2} spacing={2}>
                  <Grid item xs={6}>
                    <TextFieldElement
                      control={control}
                      name="displayInitialMessageOrPrompt"
                      label="Initial Agent Message or Prompt to show"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <SelectElement
                      control={control}
                      name="displayStartMessageOrPrompt"
                      label="Show this as a message or use as an initial prompt?"
                      size="small"
                      fullWidth
                      options={[
                        { id: "message", label: "Message" },
                        { id: "prompt", label: "Prompt" },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <CheckboxElement
                      control={control}
                      name="displayHideInitialPrompt"
                      label="Hide prompt (just show response)"
                      size="small"
                    />
                  </Grid>
                </Grid>
                <Grid container mt={1} spacing={2}>
                  <Grid item xs={3}>
                    <CheckboxElement
                      control={control}
                      name="createConversationOnFirstChat"
                      label="Create conversations"
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <SelectElement
                      control={control}
                      name="customerEmailCaptureMode"
                      label="Email capture mode"
                      size="small"
                      fullWidth
                      options={[
                        {
                          id: LLMEmailCaptureMode.HIDE,
                          label: "Hide (unless needed)",
                        },
                        {
                          id: LLMEmailCaptureMode.OPTIONAL,
                          label: "Optional (shown, but optional)",
                        },
                        {
                          id: LLMEmailCaptureMode.REQUIRED,
                          label: "Require valid email to chat",
                        },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={5}>
                    <TextFieldElement
                      control={control}
                      name="customerEmailCapturePlaceholder"
                      label="Email capture placeholder text"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>

                {/*
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="displayPromptTemplate"
                    label="First User Prompt Template (remember to include {{prompt}} where you want the users prompt to be inserted)"
                    size="small"
                    fullWidth
                  />
                </Grid>
                */}
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="displayFollowOnPrompts"
                    label="Follow on prompts (buttons shown after a response to keep the conversation going, add multiple separated by |)"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="displayActions"
                    label="Inline Response Call to Actions (Advanced: see documentation for details)"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <CheckboxElement
                    control={control}
                    name="displayShowSaveButton"
                    label="Show save button"
                    size="small"
                  />
                  <CheckboxElement
                    control={control}
                    name="displayShowEmailButton"
                    label="Show email button"
                    size="small"
                  />
                  <CheckboxElement
                    control={control}
                    name="displayShowCallToAction"
                    label="Show Call to Action button"
                    size="small"
                  />
                  <CheckboxElement
                    control={control}
                    name="displayScrollToEnd"
                    label="Autoscroll responses"
                    size="small"
                  />
                </Grid>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={3}>
                    <TextFieldElement
                      control={control}
                      name="displayCallToActionButtonText"
                      label="Call to Action button text"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldElement
                      control={control}
                      name="displayCallToActionEmailAddress"
                      label="Call to Action email address"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextFieldElement
                      control={control}
                      name="displayCallToActionEmailSubject"
                      label="Call to Action email subject"
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <hr />

                    <Typography variant="caption">
                      For embedding in some websites a static page is required
                      (eg. IFramely compatible sites like Canva). Clicking this
                      button creates a static page for that purpose.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      onClick={() => createStaticHTMLPage()}
                      variant="outlined"
                      size="small"
                    >
                      Create static page link
                    </Button>
                    &nbsp;&nbsp;
                    <TextField
                      label="Height of static page (px)"
                      sx={{ width: 175 }}
                      size="small"
                      value={staticHeight}
                      onChange={(e) => setStaticHeight(e.target.value)}
                    />
                    {staticUrl && (
                      <>
                        &nbsp;&nbsp;
                        <Typography variant="caption">{staticUrl}</Typography>
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(staticUrl);
                          }}
                          size="small"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </>
                    )}
                    <hr />
                  </Grid>
                </Grid>
              </>
            )}

            {selectedTab === 2 && (
              <>
                <Grid item xs={12} mt={2}>
                  <TextFieldElement
                    control={control}
                    name="data"
                    label="Data"
                    size="small"
                    fullWidth
                    multiline
                    rows={25}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <Button
                    onClick={() =>
                      navigator.clipboard.writeText(
                        `fetch(
"https://api.llmasaservice.io/agents/${id}",
{
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "x-api-key": "${apiKey1}", // api key 1 from the Integration API page if available
    "x-project-id": "${project?.id}"
  },
  body: JSON.stringify({
    action: "data",
    data: "new data here" // add your new data string here
  })
}
)
.then((response) => response.text())
.then((result) => console.log(result))
.catch((error) => console.error("Error:", error));`
                      )
                    }
                    variant="text"
                  >
                    Copy JS Fetch Update code
                  </Button>
                </Grid>
              </>
            )}

            {selectedTab === 3 && (
              <>
                <Grid item xs={12} mt={2}>
                  <Typography variant="body2">
                    This is a beta feature. Please give feedback and report
                    ideas for improvements to support@llmasaservice.io.
                  </Typography>
                  <hr />
                  <Typography variant="h6" mb={2}>
                    Retrieval Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextFieldElement
                        control={control}
                        type="number"
                        name="ragQueryLimit"
                        label="Query Limit"
                        size="small"
                        fullWidth
                        error={!!errors.ragQueryLimit}
                        helperText={errors.ragQueryLimit?.message}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextFieldElement
                        control={control}
                        type="number"
                        name="ragRankLimit"
                        label="Rank Limit"
                        size="small"
                        fullWidth
                        error={!!errors.ragRankLimit}
                        helperText={errors.ragRankLimit?.message}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <CheckboxElement
                        control={control}
                        name="ragIncludeCitations"
                        label="Include citations"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <CheckboxElement
                        control={control}
                        name="ragSkipChecksOnContext"
                        label="Skip checks"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ padding: 2 }}>
                        <Button
                          onClick={handleSubmit(onSubmit)}
                          variant={"contained"}
                          sx={{ marginRight: 2 }}
                        >
                          Save and Close
                        </Button>
                        <Button onClick={() => reset()} variant={"outlined"}>
                          Reset
                        </Button>
                        <Button onClick={handleClose} variant={"outlined"}>
                          Cancel
                        </Button>
                      </Box>
                    </Grid>{" "}
                  </Grid>

                  <hr />
                  <Typography variant="h6">Indexed Documents</Typography>
                  <List>
                    {documentsData?.data?.agentDocumentsByAgentId?.items.map(
                      (doc) => (
                        <ListItem key={doc?.id}>
                          <ListItemText
                            primary={doc?.fileName ?? doc?.location}
                            secondary={
                              doc?.fileType
                                ? `${doc?.status} - ${doc?.fileType}, ${
                                    doc?.fileSize
                                  } bytes (splitter: ${
                                    doc?.ragSplitter || "default"
                                  }, chunk size: ${
                                    doc?.ragChunkSize || 1000
                                  }, overlap: ${
                                    doc?.ragChunkOverlap ?? "unknown"
                                  })`
                                : `${doc?.status}`
                            }
                          />
                          <Box display="flex" justifyContent="flex-end">
                            {!deleting || confirmDelete !== doc?.id ? (
                              <ListItemButton
                                onClick={async () => {
                                  if (confirmDelete === doc?.id) {
                                    setDeleting(true);
                                    await API.post(
                                      "eventapi",
                                      "/deleteDocumentFromAgent",
                                      {
                                        body: {
                                          projectId: doc?.projectId,
                                          agentId: doc?.agentId,
                                          agentDocumentId: doc?.id,
                                        },
                                      }
                                    );

                                    queryClient.invalidateQueries({
                                      queryKey: [
                                        "LLMAgentDocuments",
                                        { agentId: id },
                                      ],
                                    });
                                    setDeleting(false);
                                    setConfirmDelete("");
                                  } else {
                                    setConfirmDelete(doc?.id ?? "");
                                  }
                                }}
                              >
                                {confirmDelete === (doc?.id ?? "")
                                  ? "Confirm Delete"
                                  : "Delete"}
                              </ListItemButton>
                            ) : (
                              <Typography variant="body2">
                                Deleting...
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                      )
                    )}
                  </List>

                  <hr />
                  <Typography variant="h6" mb={2}>
                    Add/Replace Documents
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FileUploader
                        agentId={id ?? ""}
                        projectId={
                          agentData?.data?.getLLMAgent?.projectId ??
                          project?.id ??
                          ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12}></Grid>
                    <Grid item xs={12}>
                      <UrlScrape
                        agentId={id ?? ""}
                        projectId={
                          agentData?.data?.getLLMAgent?.projectId ??
                          project?.id ??
                          ""
                        }
                        documents={
                          documentsData as
                            | GraphQLResult<AgentDocumentsByAgentIdQuery>
                            | undefined
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="caption">
                      Or, Upload new or replace existing document by Public API
                    </Typography>
                    <br />
                    <Button
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `fetch("https://api.llmasaservice.io/agents/${id}", {
  method: 'POST',
  headers:  {
        "Content-Type": "application/json",
        "x-api-key": "${apiKey1}", // key 1 from the Integration API page if available
        "x-project-id": "${project?.id}"
    },
  body: JSON.stringify({
     "action": "document",
     "location": "[http(s) url to your document]", // this needs to be a publicly accessible URL that a plain HTTP GET will return
     "ragSplitter": "default", // use values "default", "newline" (best for CSV files), "blankline" (best for files where there is a blankline between sections)
     "ragChunkSize": 1000, // the number of characters MAXIMUM in each chunk. The default is 1000, but you can increase this to 5000 or even 10000 if you want to get more context in each chunk. This is the number of characters in the text, not the number of tokens.
     "ragChunkOverlap": 10 // the number of characters to overlap between chunks. The default is 10, but you can increase this to 100 or even 1000 if you want to get more context in each chunk. This is the number of characters in the text, not the number of tokens.
})
                      })
  .then(response => response.text())
  .then(result => console.log(result))
  .catch(error => console.log('error', error));`
                        )
                      }
                      variant="text"
                    >
                      Copy JS Fetch Upload API code
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}

            {selectedTab === 4 && (
              <>
                <Grid item xs={12} mt={2}>
                  <Typography variant="body2">
                    This is a beta feature. Please give feedback and report
                    ideas for improvements to support@llmasaservice.io.
                  </Typography>
                  <hr />
                  <Typography variant="h6" mb={2}>
                    MCP Server Settings
                  </Typography>

                  <List>
                    {agentData?.data?.getLLMAgent?.mcpServers?.items
                      .filter(
                        (client: any) =>
                          showDeletedMCPs || client?.status === "active"
                      )
                      .map((client: any) => (
                        <ListItem key={client?.id}>
                          <ListItemText
                            primary={
                              client?.status !== "active" ? (
                                <span
                                  style={{ textDecoration: "line-through" }}
                                >
                                  {client?.url}
                                </span>
                              ) : (
                                client?.url
                              )
                            }
                            secondary={`Executed on the: ${client?.executionMode || 'CLIENT'}`}
                          />
                          <Button
                            variant="text"
                            color={
                              client?.status === "active" ? "error" : "primary"
                            }
                            size="small"
                            onClick={async () => {
                              const action =
                                client?.status === "active"
                                  ? "delete"
                                  : "restore";
                              if (
                                window.confirm(
                                  `Are you sure you want to ${action} this MCP server: ${client?.sseUrl}?`
                                )
                              ) {
                                try {
                                  await API.graphql(
                                    graphqlOperation(updateLLMAgentMCP, {
                                      input: {
                                        id: client?.id,
                                        status:
                                          client?.status === "active"
                                            ? "deleted"
                                            : "active",
                                      },
                                    })
                                  );

                                  // Refresh the data
                                  queryClient.invalidateQueries({
                                    queryKey: ["agents", { id: id }],
                                  });
                                } catch (error) {
                                  logError(
                                    `Error ${action}ing MCP client`,
                                    error as any
                                  );
                                }
                              }
                            }}
                          >
                            {client?.status === "active" ? "Delete" : "Restore"}
                          </Button>
                        </ListItem>
                      ))}
                  </List>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Button
                      onClick={() => setOpenMCPDialog(true)}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 2 }}
                    >
                      Add MCP Server
                    </Button>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showDeletedMCPs}
                          onChange={(e) => setShowDeletedMCPs(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Show Deleted"
                    />
                  </Box>
                </Grid>
              </>
            )}

            <Dialog
              open={openMCPDialog}
              onClose={() => {
                setOpenMCPDialog(false);
                setMcpUrl("");
                setMcpApiKey("");
                setShowMcpApiKey(false);
                setExecutionMode("CLIENT");
              }}
            >
              <DialogTitle>Add MCP Server</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Enter the URL of the MCP server to add to this agent.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  id="mcpUrl"
                  label="MCP Server URL"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={mcpUrl}
                  onChange={(e) => setMcpUrl(e.target.value)}
                  error={mcpUrlError}
                  helperText={mcpUrlError ? "Invalid URL" : ""}
                />
                <Box sx={{ position: 'relative', mt: 1 }}>
                  <TextField
                    margin="dense"
                    id="mcpApiKey"
                    label="API Key (optional)"
                    type={showMcpApiKey ? "text" : "password"}
                    fullWidth
                    variant="standard"
                    value={mcpApiKey}
                    onChange={(e) => setMcpApiKey(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowMcpApiKey(!showMcpApiKey)}
                          edge="end"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          {showMcpApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
                <FormControl fullWidth margin="dense" variant="standard">
                  <InputLabel id="execution-mode-label">Execution Mode</InputLabel>
                  <Select
                    labelId="execution-mode-label"
                    id="executionMode"
                    value={executionMode}
                    onChange={(e) => setExecutionMode(e.target.value)}
                    label="Execution Mode"
                  >
                    <MenuItem value="CLIENT">CLIENT</MenuItem>
                    <MenuItem value="SERVER">SERVER</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => {
                  setOpenMCPDialog(false);
                  setMcpUrl("");
                  setMcpApiKey("");
                  setShowMcpApiKey(false);
                  setExecutionMode("CLIENT");
                }}>Cancel</Button>
                <Button
                  onClick={async () => {
                    if (!id) return;

                    if (!isValidUrl(mcpUrl)) {
                      setMcpUrlError(true);
                      return;
                    }

                    await mcpCreateMutation.mutateAsync({ 
                      mcpUrl, 
                      executionMode, 
                      accessToken: mcpApiKey || undefined 
                    });
                  }}
                >
                  Add
                </Button>
              </DialogActions>
            </Dialog>

            {selectedTab === 5 && (
              <>
                <Grid item xs={12} mt={2}>
                  <TextField
                    label="Style Text"
                    size="small"
                    fullWidth
                    multiline
                    rows={25}
                    onChange={(e) => setStyleText(e.target.value)}
                  />
                </Grid>
              </>
            )}

            {selectedTab < 3 && (
              <Box sx={{ padding: 2 }}>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant={"contained"}
                  sx={{ marginRight: 2 }}
                >
                  Save and Close
                </Button>
                <Button onClick={() => reset()} variant={"outlined"}>
                  Reset
                </Button>
                <Button onClick={handleClose} variant={"outlined"}>
                  Cancel
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ margin: 2, borderRadius: 3, border: "1px solid #444" }}
            >
              <Typography padding={2} variant="h6">
                Agent Preview
              </Typography>
              {agentData?.data?.getLLMAgent && (
                <ChatPanel
                  project_id={project?.id || ""}
                  service={groupIdWatch ?? null}
                  url={url}
                  title={titleWatch ?? "Chat Agent"}
                  theme={themeWatch === "light" ? "light" : "dark"}
                  height={heightWatch ?? "75vh"}
                  width={widthWatch ?? "100%"}
                  cssUrl={styleText === "" ? cssUrlWatch ?? "" : styleText}
                  promptTemplate={promptTemplateWatch ?? "{{prompt}}"}
                  initialMessage={
                    startWatch === "message" ? initialWatch ?? "" : undefined
                  }
                  initialPrompt={
                    startWatch === "prompt" ? initialWatch ?? "" : undefined
                  }
                  followOnQuestions={followOnPromptsWatch?.split("|") ?? []}
                  actions={getActionsArraySafely(actionsWatch ?? "")}
                  agent={agentData?.data?.getLLMAgent?.id ?? null}
                  placeholder={displayPlaceholderWatch ?? "Type a message"}
                  hideInitialPrompt={displayHideInitialPromptWatch ?? true}
                  data={dataWatch ? [{ key: "data", data: dataWatch }] : []}
                  showEmailButton={displayShowEmailButtonWatch ?? true}
                  showSaveButton={displayShowSaveButtonWatch ?? true}
                  showCallToAction={displayShowCallToActionWatch ?? false}
                  callToActionButtonText={
                    displayCallToActionButtonTextWatch ?? "Submit"
                  }
                  callToActionEmailAddress={
                    displayCallToActionEmailAddressWatch ?? ""
                  }
                  callToActionEmailSubject={
                    displayCallToActionEmailSubjectWatch ??
                    "Agent CTA Submitted"
                  }
                  scrollToEnd={displayScrollToEndWatch ?? false}
                  //- clearFollowOnQuestionsNextPrompt = false,
                  //- thumbsUpClick,
                  //- thumbsDownClick,
                  //- markdownClass = null,
                  //- historyChangedCallback = null,
                  //- followOnPrompt = "",
                  //- messages = [],
                  createConversationOnFirstChat={
                    createConversationOnFirstChatWatch ?? true
                  }
                  customerEmailCaptureMode={
                    customerEmailCaptureModeWatch ?? LLMEmailCaptureMode.HIDE
                  }
                  customerEmailCapturePlaceholder={
                    customerEmailCapturePlaceholderWatch ??
                    "Please enter your email..."
                  }
                  //mcpServers={}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default AgentForm;
